import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import cn from 'classnames';
import { ElementThemeUnion } from 'constants/routes';
import { IMediaFile } from 'interfaces';

import PreviewMediaFiles from 'components/PreviewMediaFiles';
import { getMediaFiles, isVideo } from 'utils';

import ConfirmModal from '../Modal/ConfirmModal';

import DragOverlay from './DragOverlay';
import DropzoneContent from './DropzoneContent';

interface IDropzoneProps {
  files: IMediaFile[];
  mediaOrder?: number[];
  acceptedFormat?: string;
  withoutPreview?: boolean;
  theme?: ElementThemeUnion;
  classNames?: { body?: string; preview?: string; text?: string };
  placeholder?: string;
  onAdd: (file: IMediaFile[]) => void;
  onDelete: (file: IMediaFile) => void;
  onPreviewClick?: (files: IMediaFile[], mediaOrder?: number[]) => void;
  onMediaReorder?: (media: IMediaFile[]) => void;
}

const Dropzone: FC<IDropzoneProps> = ({
  files,
  mediaOrder,
  withoutPreview,
  theme = 'light',
  classNames,
  placeholder,
  onPreviewClick,
  onDelete,
  onAdd,
  onMediaReorder = () => {},
}) => {
  const [isPreview, setPreview] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [deleteModal, setDeleteModal] = useState<IMediaFile>();

  const previewList = useMemo(() => getMediaFiles(files), [files]);

  const backgroundClass = theme === 'light' ? 'bg-light' : 'bg-dark';
  const title = placeholder ?? 'Перетащите сюда фото или видео';

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles?.length) {
        const mediaFiles: IMediaFile[] = acceptedFiles.map((file) => ({
          file: file,
          type: isVideo(file.name) ? 'video' : 'image',
          url: URL.createObjectURL(file),
        }));

        onAdd(mediaFiles);
      }

      setIsDragging(false);
    },
    [onAdd]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      const clipboardFiles = Array.from(event.clipboardData?.files || []);

      if (clipboardFiles.length > 0) {
        const mediaFiles: IMediaFile[] = clipboardFiles.map((file) => ({
          file,
          type: isVideo(file.name) ? 'video' : 'image',
          url: URL.createObjectURL(file),
        }));

        onAdd(mediaFiles);
      }
    },
    [onAdd]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
  });

  const handlePreviewClick = () => {
    if (onPreviewClick) {
      onPreviewClick(previewList, mediaOrder);
    }
  };

  const handleConfirmDelete = () => {
    if (deleteModal) {
      onDelete(deleteModal);
      setDeleteModal(undefined);
    }
  };

  const renderDropzoneBody = () => {
    return (
      isPreview && (
        <>
          {deleteModal && (
            <ConfirmModal
              onConfirm={handleConfirmDelete}
              onClose={() => setDeleteModal(undefined)}
            />
          )}

          <PreviewMediaFiles
            files={previewList}
            mediaOrder={mediaOrder}
            isDelete
            isExpanded
            onClick={handlePreviewClick}
            onDelete={setDeleteModal}
            showFullImage={false}
            visible={2}
            size="m"
            useDnD
            onMediaReorder={onMediaReorder}
            className={classNames?.preview}
          />
        </>
      )
    );
  };

  useEffect(() => {
    setPreview(!!previewList.length && !withoutPreview);
  }, [previewList]);

  useEffect(() => {
    const onPaste = (event: Event) => {
      if (event instanceof ClipboardEvent) {
        handlePaste(event);
      }
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [handlePaste]);

  useEffect(() => {
    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();

      if (!isDragging) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (event: DragEvent) => {
      if (event.relatedTarget === null) {
        setIsDragging(false);
      }
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);

    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
    };
  }, []);

  return (
    <div
      className={cn(
        'w-full py-3 px-4 rounded-md cursor-pointer',
        backgroundClass,
        classNames?.body
      )}
      {...getRootProps({
        onClick: (event) => (event.defaultPrevented ? null : open()),
      })}
    >
      {isDragging && <DragOverlay title={title} />}
      <input {...getInputProps()} />
      <DropzoneContent
        isPreview={isPreview}
        title={title}
        renderDropzoneBody={renderDropzoneBody}
      />
    </div>
  );
};

export default memo(Dropzone);
